import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Apple, Download } from "lucide-react";
import { BrandMark, Wordmark } from "./BrandMark";

const NAV_ITEMS = [
    { label: "How it works", hash: "#how-it-works" },
    { label: "Features", hash: "#features" },
    { label: "Use cases", hash: "#use-cases" },
    { label: "Safety", hash: "#safety" },
    { label: "FAQ", hash: "#faq" },
];

export default function TopNav({ variant = "home" }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isLegal = variant === "legal" || location.pathname !== "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleAnchor = (e, hash) => {
        if (isLegal) {
            e.preventDefault();
            navigate("/" + hash);
        }
    };

    return (
        <header
            data-testid="topnav"
            className={`sticky top-0 z-50 transition-[backdrop-filter,background-color,border-color,box-shadow] duration-300 ${
                scrolled || isLegal
                    ? "backdrop-blur-md bg-[rgba(246,241,231,0.82)] border-b border-[rgba(231,224,211,0.9)] shadow-[0_1px_0_rgba(14,27,42,0.04)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 sm:h-[72px] items-center justify-between">
                    <Link
                        to="/"
                        data-testid="topnav-logo"
                        className="group inline-flex items-center gap-2.5 ring-focus rounded-md"
                        aria-label="BetweenUs — home"
                    >
                        <BrandMark size={34} />
                        <Wordmark />
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {NAV_ITEMS.map((item) => {
                            const href = isLegal ? "/" + item.hash : item.hash;
                            return (
                                <a
                                    key={item.hash}
                                    href={href}
                                    onClick={(e) => handleAnchor(e, item.hash)}
                                    data-testid={`topnav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="px-3 py-2 text-[13px] font-medium tracking-[-0.01em] text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] rounded-[10px] transition-colors"
                                >
                                    {item.label}
                                </a>
                            );
                        })}
                        <Link
                            to="/blog"
                            data-testid="topnav-link-blog"
                            className={`px-3 py-2 text-[13px] font-medium tracking-[-0.01em] rounded-[10px] transition-colors ${
                                location.pathname === "/blog"
                                    ? "text-[var(--brand-ink)]"
                                    : "text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)]"
                            }`}
                        >
                            Blog
                        </Link>
                    </nav>

                    <div className="hidden md:flex items-center gap-2">
                        <a
                            href={isLegal ? "/#download" : "#download"}
                            data-testid="topnav-download-button"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[13px] font-semibold tracking-[-0.01em] shadow-[0_10px_24px_rgba(14,27,42,0.14)] hover:bg-[var(--brand-navy-2)] hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98] transition-[background-color,transform,box-shadow] duration-200"
                        >
                            <Download size={15} strokeWidth={2} />
                            Download
                        </a>
                    </div>

                    <button
                        className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full bg-white border border-brand text-ink ring-focus"
                        data-testid="topnav-mobile-menu-button"
                        onClick={() => setOpen((v) => !v)}
                        aria-expanded={open}
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {/* Mobile panel */}
            <div
                data-testid="topnav-mobile-panel"
                className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
                    open
                        ? "max-h-[420px] opacity-100"
                        : "max-h-0 opacity-0 pointer-events-none"
                }`}
            >
                <div className="mx-4 mb-4 rounded-[18px] bg-white border border-brand shadow-[var(--shadow-soft)] p-3">
                    <nav className="flex flex-col">
                        {NAV_ITEMS.map((item) => {
                            const href = isLegal ? "/" + item.hash : item.hash;
                            return (
                                <a
                                    key={item.hash}
                                    href={href}
                                    onClick={(e) => {
                                        setOpen(false);
                                        handleAnchor(e, item.hash);
                                    }}
                                    data-testid={`topnav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="px-3 py-3 rounded-[12px] text-[14px] font-medium text-ink hover:bg-[rgba(48,140,138,0.08)]"
                                >
                                    {item.label}
                                </a>
                            );
                        })}
                        <Link
                            to="/blog"
                            onClick={() => setOpen(false)}
                            data-testid="topnav-mobile-link-blog"
                            className="px-3 py-3 rounded-[12px] text-[14px] font-medium text-ink hover:bg-[rgba(48,140,138,0.08)]"
                        >
                            Blog
                        </Link>
                    </nav>
                    <a
                        href={isLegal ? "/#download" : "#download"}
                        data-testid="topnav-mobile-download-button"
                        onClick={() => setOpen(false)}
                        className="mt-2 inline-flex w-full items-center justify-center gap-2 h-11 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-sm font-semibold"
                    >
                        <Apple size={15} /> Download BetweenUs
                    </a>
                </div>
            </div>
        </header>
    );
}
